import React, {useEffect} from 'react';
import { arrayOf, bool, oneOf, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import { keys, map, pathOr, reduce, reverse } from 'ramda';
import sortBy from 'lodash/sortBy';

import { propTypes, LINE_ITEM_IS_BOOKING_FULL_DAY } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { getMorningOrAfternoonByDate, getMorningOrAfternoonLabelByDate, moment } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import { getQuantity, txHasBeenAccepted } from '../../util/transactions';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  AvatarMedium,
  NamedLink,
  Page,
  PaginationLinks,
  TabNav,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  IconSpinner,
  UserDisplayName,
  ResponsiveImage,
  IconArrowHead,
} from '../../components';
import { TopbarContainer, NotFoundPage } from '../../containers';

import { loadData } from './ReservationsPage.duck';
import css from './ReservationsPage.css';
import AlertBadge from '../../components/AlertBadge';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

const sortedTransactions = txs =>
  sortBy(txs, tx => {
    return tx.booking ? tx.booking.attributes.start : null;
  });

const formatDate = (intl, date, isEveningMode = false) => {
  const dateWithTz = moment(date);
  return {
    date: intl.formatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    timeOfTheDay: isEveningMode ? getMorningOrAfternoonLabelByDate(dateWithTz, intl) : getMorningOrAfternoonByDate(dateWithTz, intl),
  };
};

const sortedTransactionsByDate = (txs, intl) => {
  return reduce((acc, tx) => {
    const dateKey = formatDate(intl, tx.booking.attributes.start).date;
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(tx);

    return acc;
  }, {}, txs);
};

const BookingInfoMaybe = props => {
  const { isOrder, intl, tx } = props;

  const bookingPrice = isOrder ? tx.attributes.payinTotal : tx.attributes.payoutTotal;
  const priceMessage = intl.formatMessage({ id: isOrder ? 'ReservationsPage.price' : 'ReservationsPage.income' });
  const price = formatMoney(intl, bookingPrice);

  const quantity = getQuantity(tx);
  const quantityMessage = intl.formatMessage({id: 'ReservationsPage.quantity' }, { quantity: quantity.total });

  return (
    <div className={css.bookingInfoWrapper}>
      <span className={css.itemQuantity}>{quantityMessage}</span>
      {!isOrder ? (
        <div className={css.itemPriceContainer}>
          <div className={css.itemPriceMessage}>{priceMessage}</div>
          <div className={css.itemPrice}>{price}</div>
        </div>
      ) : null}
    </div>
  );
};

BookingInfoMaybe.propTypes = {
  intl: intlShape.isRequired,
  isOrder: bool.isRequired,
  tx: propTypes.transaction.isRequired,
};

export const ReservationItem = props => {
  const { type, tx, intl } = props;
  const { customer, provider, listing } = tx;
  const isOrder = type === 'order';
  const isBookingFullDay = tx.attributes.lineItems.find(item => item.code === LINE_ITEM_IS_BOOKING_FULL_DAY && !item.reversal);
  const bookingDate = formatDate(intl, tx.booking.attributes.start, pathOr(false, ['booking', 'attributes', 'isEveningMode'], tx));
  const timeOfTheDayMessage = isBookingFullDay ? intl.formatMessage({ id: 'ReservationsPage.fullday' }) : bookingDate.timeOfTheDay;
  const otherUser = isOrder ? provider : customer;
  const otherUserRole = intl.formatMessage({ id: isOrder ? 'ReservationsPage.provider' : 'ReservationsPage.customer' });
  const otherUserDisplayName = <UserDisplayName className={css.userDisplayName} user={otherUser} intl={intl} />;
  const isOtherUserBanned = otherUser.attributes.banned;
  const addressMessage = intl.formatMessage({ id: 'ReservationsPage.address' });
  const canShowAddress = tx && txHasBeenAccepted(tx);
  const listingAddress = pathOr('', ['attributes', 'publicData', 'location', 'address'], listing);
  const listingId = pathOr('', ['id', 'uuid'], listing);
  const listingTitle = pathOr('', ['attributes', 'title'], listing);
  const slug = createSlug(listingTitle);
  const listingImg = listing.images && listing.images.length > 0 ? listing.images[0] : null;
  const hasNotBeenOpened = !isOrder ? pathOr(false, ['attributes', 'metadata', 'hasNotBeenOpenedByProvider'], tx) :
    pathOr(false, ['attributes', 'metadata', 'hasNotBeenOpenedByCustomer'], tx);
  const linkClasses = classNames(css.itemLink, {
    [css.bannedUserLink]: isOtherUserBanned,
  });

  return (
    <div className={css.item}>
      {isOrder ? (
        <NamedLink
          name={isOrder ? 'OrderDetailsPage' : 'SaleDetailsPage'}
          params={{ id: tx.id.uuid }}
        >
          {isOrder ? (
            <div className={css.itemTimeOfTheDayOrder}>{timeOfTheDayMessage}</div>
          ) : null}
          <ResponsiveImage
            rootClassName={css.itemListingImage}
            alt={"listingImg"}
            image={listingImg}
            variants={['landscape-crop']}
          />
        </NamedLink>
      ) : null}
      <div className={css.itemInfo}>
        <div className={css.itemInfoTopContainer}>
          <div className={css.itemBookingInfoContainer}>
            <NamedLink
              className={linkClasses}
              name={isOrder ? 'OrderDetailsPage' : 'SaleDetailsPage'}
              params={{ id: tx.id.uuid }}
            >
              {!isOrder ? (
                <div className={css.itemTimeOfTheDay}>{timeOfTheDayMessage}</div>
              ) : null}
              <div className={css.itemUsername}>
                {hasNotBeenOpened && (<AlertBadge />)}
                <span className={css.itemOtherUserRole}>{otherUserRole}</span>
                {otherUserDisplayName}
              </div>
              <div className={css.itemBookingInfo}>
                <BookingInfoMaybe intl={intl} isOrder={isOrder} tx={tx} />
              </div>
              {isOrder && canShowAddress ? (
                <div className={css.itemAddressContainer}>
                  <span className={css.itemAddressMessage}>{addressMessage}</span>
                  <address className={css.itemAddress}>{listingAddress}</address>
                </div>
              ) : null}
            </NamedLink>
          </div>
          <AvatarMedium user={otherUser} />
        </div>
        {isOrder ? (
          <div className={css.itemListingContainer}>
            <NamedLink
              className={css.listingLink}
              name="ListingPage"
              params={{ id: listingId, slug }}
            >
              {listingImg && (
                <ResponsiveImage
                  rootClassName={css.listingImg}
                  alt={"listingImg"}
                  image={listingImg}
                  variants={['square-small']}
                />
                )}
              <span className={css.listingTitle}>
                {listingTitle}
              </span>
              <IconArrowHead className={css.listingArrow} direction='right' />
            </NamedLink>
          </div>
        ) : null}
      </div>
    </div>
  );
};

ReservationItem.propTypes = {
  type: oneOf(['order', 'sale']).isRequired,
  tx: propTypes.transaction.isRequired,
  intl: intlShape.isRequired,
};

export const upcomingTab = 'upcoming';
export const pastTab = 'past';
export const pendingTab = 'pending';
export const othersTab = 'others';

export const ReservationsPageComponent = props => {
  const {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    intl,
    pagination,
    params,
    scrollingDisabled,
    transactions,
    currentUserHasListings,
    bookingsIncomeBadge,
    bookingsRequestBadge,
    bookingsOthersBadge,
    updateProfile,
  } = props;
  const { tab } = params;
  const isUpcomingTab = tab === upcomingTab;
  const isPastTab = tab === pastTab;
  const isPendingTab = tab === pendingTab;
  const isOthersTab = tab === othersTab;
  useEffect(() => {
    const bookingTabBadgeToUpdate = getBookingTabBadgeToUpdate();
    if (!bookingTabBadgeToUpdate) return;

    updateProfile({privateData: {...bookingTabBadgeToUpdate}});
  }, [tab]);
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const getBookingTabBadgeToUpdate = () => {
    switch (tab) {
      case pendingTab:
        return bookingsRequestBadge > 0 ? {bookingsRequestBadge: 0} : null;
      case upcomingTab:
        return bookingsIncomeBadge > 0 ? {bookingsIncomeBadge: 0} : null;
      case othersTab:
        return bookingsOthersBadge > 0 ? {bookingsOthersBadge: 0} : null;
      default:
        return null;
    }
  }
  const validTab = isPendingTab || isPastTab || isUpcomingTab || isOthersTab;
  if (!validTab) {
    return <NotFoundPage />;
  }

  const title = intl.formatMessage({ id: 'ReservationsPage.title' });

  const toTxItem = tx => {
    const currentUserId = pathOr(null, ['id', 'uuid'], ensuredCurrentUser);
    const txProviderId = pathOr(null, ['provider', 'id', 'uuid'], tx);
    const type = currentUserId && txProviderId && currentUserId === txProviderId ? 'sale' : 'order';

    return (
      <li key={tx.id.uuid} className={css.listItem}>
        <ReservationItem type={type} tx={tx} intl={intl} />
      </li>
    );
  };

  const error = fetchOrdersOrSalesError ? (
    <p className={css.error}>
      {intl.formatMessage({ id: 'ReservationsPage.fetchFailed' })}
    </p>
  ) : null;

  const noResults =
    !fetchInProgress && transactions.length === 0 && !fetchOrdersOrSalesError ? (
      <li key="noResults" className={css.noResults}>
        {intl.formatMessage(
          { id: isUpcomingTab
              ? 'ReservationsPage.noUpcomingReservations'
              : isPastTab
              ? 'ReservationsPage.noPastReservations'
              : 'ReservationsPage.noPendingReservations'
          })}
      </li>
    ) : null;

  const hasTransactions =
    !fetchInProgress && transactions.length > 0;
  const pagingLinks =
    hasTransactions && pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="ReservationsPage"
        pagePathParams={params}
        pagination={pagination}
      />
    ) : null;

  const tabs = [
    {
      text: (intl.formatMessage({ id: 'ReservationsPage.pendingTabTitle'})),
      selected: isPendingTab,
      notificationCount: bookingsRequestBadge,
      linkProps: {
        name: 'ReservationsPage',
        params: { tab: 'pending' },
      },
    },
    {
      text: (intl.formatMessage({ id: 'ReservationsPage.upcomingTabTitle'})),
      selected: isUpcomingTab,
      notificationCount: bookingsIncomeBadge,
      linkProps: {
        name: 'ReservationsPage',
        params: { tab: 'upcoming' },
      },
    },
    {
      text: (intl.formatMessage({ id: 'ReservationsPage.pastTabTitle'})),
      selected: isPastTab,
      linkProps: {
        name: 'ReservationsPage',
        params: { tab: 'past' },
      },
    },
    {
      text: (intl.formatMessage({ id: 'ReservationsPage.othersTabTitle'})),
      selected: isOthersTab,
      notificationCount: bookingsOthersBadge,
      linkProps: {
        name: 'ReservationsPage',
        params: { tab: 'others' },
      },
    },
  ];
  const nav = <TabNav className={css.tabs} tabs={tabs} />;
  const transactionsByDate = sortedTransactionsByDate(isPastTab ? reverse(transactions) : transactions, intl);
  const dates = keys(transactionsByDate);
  const items = map((date) => {
    return (
      <li key={date} className={css.listDate}>
        <div className={css.dateContainer}>{date}</div>
        <ul className={css.itemList}>
          {map(toTxItem, isPastTab ? sortedTransactions(transactionsByDate[date]) : transactionsByDate[date])}
        </ul>
      </li>
    )
  }, dates)

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer className={css.topbar} currentPage="ReservationsPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperSideNav>
          <h1 className={css.title}>
            <FormattedMessage id="ReservationsPage.title" />
          </h1>
          {nav}
        </LayoutWrapperSideNav>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          {error}
          <ul className={css.dateList}>
            {!fetchInProgress ? (
              items
            ) : (
              <li className={css.listItemsLoading}>
                <IconSpinner />
              </li>
            )}
            {noResults}
          </ul>
          {pagingLinks}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

ReservationsPageComponent.defaultProps = {
  currentUser: null,
  currentUserHasOrders: null,
  fetchOrdersOrSalesError: null,
  pagination: null,
  sendVerificationEmailError: null,
  bookingsIncomeBadge: 0,
  bookingsRequestBadge: 0,
  bookingsOthersBadge: 0,
};

ReservationsPageComponent.propTypes = {
  params: shape({
    tab: string.isRequired,
  }).isRequired,

  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  fetchOrdersOrSalesError: propTypes.error,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  transactions: arrayOf(propTypes.transaction).isRequired,
  currentUserHasListings: bool.isRequired,
  bookingsIncomeBadge: propTypes.number,
  bookingsRequestBadge: propTypes.number,
  bookingsOthersBadge: propTypes.number,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { fetchInProgress, fetchOrdersOrSalesError, pagination, transactionRefs } = state.ReservationsPage;
  const { currentUser, currentUserHasListings } = state.user;
  const bookingsIncomeBadge = pathOr(0, ['attributes', 'profile', 'privateData', 'bookingsIncomeBadge'], currentUser);
  const bookingsRequestBadge = pathOr(0, ['attributes', 'profile', 'privateData', 'bookingsRequestBadge'], currentUser);
  const bookingsOthersBadge = pathOr(0, ['attributes', 'profile', 'privateData', 'bookingsOthersBadge'], currentUser);
  return {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    transactions: sortedTransactions(getMarketplaceEntities(state, transactionRefs)),
    currentUserHasListings,
    bookingsIncomeBadge,
    bookingsRequestBadge,
    bookingsOthersBadge
  };
};

const mapDispatchToProps = {
  updateProfile
};

const ReservationsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ReservationsPageComponent);

ReservationsPage.loadData = loadData;

export default ReservationsPage;



// WEBPACK FOOTER //
// ./src/containers/ReservationsPage/ReservationsPage.js